import React, {
	useRef,
	useState,
	useEffect,
	createContext,
	useContext,
} from 'react';
import { cn } from '@lqa-frontend/ui-system';

// IMPORTANT — DO NOT add an explicit `overflow-y` (hidden / clip / etc.)
// here. CSS spec coerces conflicting overflow axes: `overflow-x: auto`
// combined with `overflow-y: clip` is silently rewritten by the browser to
// `overflow-y: hidden`, which establishes a scroll container that captures
// vertical wheel/touchpad events even when there's nothing to scroll
// vertically. With many rows (e.g. pageSize=50), the cursor is almost
// always over this element and the page becomes un-scrollable.
// Leaving `overflow-y` unspecified lets the spec coerce it to `auto`,
// which only captures wheel events when there's actual vertical overflow
// — and there isn't, because the container's height equals its content.
// Vertical clipping for the rounded border still happens via the parent
// wrappers (`overflow-clip` on `TableContainer` and the sticky-shadow
// wrapper just outside this element).
const scrollContainerClass =
	'w-full overflow-x-auto relative ' +
	'[scrollbar-color:var(--color-brand-blue-200)_transparent] ' +
	'[&::-webkit-scrollbar]:h-8 ' +
	'[&::-webkit-scrollbar-track]:bg-transparent ' +
	'[&::-webkit-scrollbar-thumb]:rounded-xs [&::-webkit-scrollbar-thumb]:bg-brand-blue-200';

export interface ITableScrollContextValue {
	isScrolled: boolean;
	isScrollEnd: boolean;
}

const TableScrollContext = createContext<ITableScrollContextValue>({
	isScrolled: false,
	isScrollEnd: true,
});

export function useTableScroll() {
	return useContext(TableScrollContext);
}

export interface ITableScrollAreaProps extends React.ComponentProps<'div'> {
	children: React.ReactNode;
	containerRef?: React.RefObject<HTMLDivElement | null>;
}

function TableScrollArea({
	children,
	className,
	containerRef: containerRefProp,
	...props
}: ITableScrollAreaProps) {
	const [isScrolled, setScrolled] = useState<boolean>(false);
	const [isScrollEnd, setScrollEnd] = useState<boolean>(false);
	const internalRef = useRef<HTMLDivElement>(null);
	const scrollRef = containerRefProp ?? internalRef;

	const handleScrollEvent = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		setScrolled(target.scrollLeft > 0);
	};

	useEffect(() => {
		const element = scrollRef.current;
		if (!element) return;

		const handleScroll = () => {
			const reachedEnd =
				element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
			setScrollEnd(reachedEnd);
		};

		handleScroll();
		element.addEventListener('scroll', handleScroll);
		return () => element.removeEventListener('scroll', handleScroll);
	}, []);

	const scrollContextValue: ITableScrollContextValue = {
		isScrolled,
		isScrollEnd,
	};

	return (
		<div
			// `overflow-clip` instead of `overflow-hidden` so this wrapper
			// doesn't establish a wheel-event-capturing scroll container.
			// Sticky-column shadows still clip correctly (clip is visually
			// equivalent to hidden); the difference is wheel events bubble
			// past instead of being absorbed.
			className={cn('relative w-full min-w-0 overflow-clip')}
			data-slot="table-scroll-wrapper"
			data-testid="table-scroll-wrapper"
		>
			<div
				ref={scrollRef}
				onScroll={handleScrollEvent}
				className={cn(scrollContainerClass, className)}
				data-slot="table-scroll-container"
				data-testid="table-scroll-container"
				data-scrolled={isScrolled}
				data-scroll-end={isScrollEnd}
				{...props}
			>
				<TableScrollContext.Provider value={scrollContextValue}>
					{children}
				</TableScrollContext.Provider>
			</div>
		</div>
	);
}

export { TableScrollArea };
